<template>
  <div class="admin-stores-view container py-4">
    <nav aria-label="breadcrumb" class="mb-3">
      <ol class="breadcrumb">
        <li class="breadcrumb-item">
          <router-link to="/admin" class="text-decoration-none">
            <font-awesome-icon icon="home" class="me-1" />
            Admin
          </router-link>
        </li>
        <li class="breadcrumb-item active" aria-current="page">Stores</li>
      </ol>
    </nav>

    <div class="d-flex justify-content-between align-items-center mb-4">
      <div>
        <h2 class="mb-0">
          <font-awesome-icon icon="store" class="me-2" />
          Stores
        </h2>
        <p class="text-muted mb-0">Abrechnungen für Apple und Google verwalten</p>
      </div>
      <div class="d-flex gap-2">
        <button
          class="btn btn-outline-primary"
          :disabled="syncing"
          @click="syncNow"
        >
          <font-awesome-icon icon="sync" class="me-2" />
          {{ syncing ? 'Sync läuft...' : 'Sync jetzt' }}
        </button>
        <BackButton fallback="admin" />
      </div>
    </div>

    <div class="btn-group mb-4" role="group" aria-label="Store Auswahl">
      <button
        type="button"
        class="btn"
        :class="selectedStore === 'google' ? 'btn-primary' : 'btn-outline-primary'"
        @click="selectedStore = 'google'"
      >
        Google
      </button>
      <button
        type="button"
        class="btn"
        :class="selectedStore === 'apple' ? 'btn-primary' : 'btn-outline-primary'"
        @click="selectedStore = 'apple'"
      >
        Apple
      </button>
    </div>

    <div>
      <div v-if="loading" class="text-center my-5">
        <div class="spinner-border" role="status">
          <span class="visually-hidden">Daten werden geladen...</span>
        </div>
      </div>

      <div v-else-if="error" class="alert alert-danger" role="alert">
        <font-awesome-icon icon="exclamation-triangle" class="me-2" />
        {{ error }}
      </div>

      <div v-else-if="years.length === 0" class="text-center py-5 text-muted">
        <font-awesome-icon icon="file" class="h1 mb-3" />
        <p>Keine {{ storeLabel }} Store-Daten gefunden.</p>
      </div>

      <div v-else class="stores-list">
        <div v-if="activeYear" class="year-switcher mb-3">
          <button
            class="btn btn-sm btn-outline-secondary"
            :disabled="!hasPrevYear"
            @click="goPrevYear"
          >
            &lt;
          </button>
          <div class="year-label">{{ activeYear.year }}</div>
          <button
            class="btn btn-sm btn-outline-secondary"
            :disabled="!hasNextYear"
            @click="goNextYear"
          >
            &gt;
          </button>
        </div>

        <div v-if="activeYear" :key="activeYear.year" class="card mb-3">
          <div class="card-body">
            <details open>
              <summary class="year-summary">
                <strong>{{ activeYear.year }}</strong>
              </summary>

              <div class="mt-3">
                <div v-for="quarter in activeYear.quarters" :key="quarter.quarter" class="quarter-card">
                  <details>
                    <summary class="quarter-summary">
                      <span>Q{{ quarter.quarter }}</span>
                      <button
                        class="btn btn-sm btn-outline-primary ms-auto"
                        :disabled="!quarter.zipAvailable || downloadingZip"
                        @click.stop="downloadZip(activeYear.year, quarter.quarter)"
                      >
                        <font-awesome-icon icon="file-archive" class="me-1" />
                        ZIP (alles)
                      </button>
                    </summary>

                    <div class="month-grid mt-3">
                        <div
                          v-for="monthInfo in quarter.months"
                          :key="monthInfo.month"
                          class="month-card"
                        >
                          <div class="month-label">{{ monthLabel(monthInfo.month) }}</div>
                        <button
                          class="btn btn-sm btn-outline-secondary"
                          :disabled="!monthInfo.csvAvailable || downloadingCsv"
                          @click="downloadCsv(activeYear.year, monthInfo.month)"
                        >
                          CSV
                        </button>
                        <button
                          class="btn btn-sm btn-outline-secondary"
                          :disabled="!monthInfo.pdfAvailable || downloadingPdf"
                          @click="downloadPdf(activeYear.year, monthInfo.month)"
                        >
                          PDF
                        </button>
                      </div>
                    </div>
                  </details>
                </div>
              </div>
            </details>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, ref, watch } from 'vue'
import BackButton from '@/components/common/BackButton.vue'
import { adminApi } from '@/api/admin'
import { showError, showSuccess } from '@/services/notification'

const selectedStore = ref('google')
const loading = ref(false)
const error = ref(null)
const syncStatus = ref({ google: false, apple: false })
const downloadingCsv = ref(false)
const downloadingPdf = ref(false)
const downloadingZip = ref(false)
const storeData = ref({
  google: { years: [] },
  apple: { years: [] }
})
const selectedYear = ref({ google: null, apple: null })

const storeLabel = computed(() => (selectedStore.value === 'apple' ? 'Apple' : 'Google'))
const activeStoreData = computed(() => storeData.value[selectedStore.value] || { years: [] })
const years = computed(() => activeStoreData.value?.years || [])
const availableYears = computed(() => [...years.value].sort((a, b) => a.year - b.year))

const activeYear = computed(() => {
  if (!availableYears.value.length) return null
  const store = selectedStore.value
  const selected = selectedYear.value[store]
  const found = availableYears.value.find((entry) => entry.year === selected)
  return found || availableYears.value[availableYears.value.length - 1]
})

const hasPrevYear = computed(() => {
  if (!activeYear.value) return false
  const index = availableYears.value.findIndex((entry) => entry.year === activeYear.value.year)
  return index > 0
})

const hasNextYear = computed(() => {
  if (!activeYear.value) return false
  const index = availableYears.value.findIndex((entry) => entry.year === activeYear.value.year)
  return index >= 0 && index < availableYears.value.length - 1
})

const syncing = computed(() => syncStatus.value[selectedStore.value])

const setDefaultYearForStore = (store, data) => {
  const yearsList = [...(data?.years || [])].sort((a, b) => a.year - b.year)
  const currentYear = new Date().getFullYear()
  const hasCurrent = yearsList.some((entry) => entry.year === currentYear)
  selectedYear.value = {
    ...selectedYear.value,
    [store]: hasCurrent ? currentYear : yearsList.at(-1)?.year || null
  }
}

const loadStoreData = async (store, { force = false } = {}) => {
  if (!force && storeData.value[store]?.years?.length) return
  loading.value = true
  error.value = null
  try {
    const data =
      store === 'apple'
        ? await adminApi.getAppleStoreEarnings()
        : await adminApi.getGoogleStoreEarnings()
    storeData.value = { ...storeData.value, [store]: data }
    setDefaultYearForStore(store, data)
  } catch (err) {
    console.error('Error loading store data:', err)
    error.value = err.response?.data?.message || 'Fehler beim Laden der Store-Daten'
  } finally {
    loading.value = false
  }
}

const goPrevYear = () => {
  if (!hasPrevYear.value || !activeYear.value) return
  const store = selectedStore.value
  const index = availableYears.value.findIndex((entry) => entry.year === activeYear.value.year)
  selectedYear.value = { ...selectedYear.value, [store]: availableYears.value[index - 1].year }
}

const goNextYear = () => {
  if (!hasNextYear.value || !activeYear.value) return
  const store = selectedStore.value
  const index = availableYears.value.findIndex((entry) => entry.year === activeYear.value.year)
  selectedYear.value = { ...selectedYear.value, [store]: availableYears.value[index + 1].year }
}

const monthLabel = (month) => {
  const date = new Date(2024, month - 1, 1)
  return new Intl.DateTimeFormat('de-DE', { month: 'short' }).format(date)
}

const downloadCsv = async (year, month) => {
  try {
    downloadingCsv.value = true
    const monthLabel = String(month).padStart(2, '0')
    const response =
      selectedStore.value === 'apple'
        ? await adminApi.downloadAppleStoreCsv(year, monthLabel)
        : await adminApi.downloadGoogleStoreCsv(year, monthLabel)
    const contentDisposition = response.headers?.['content-disposition']
    const prefix = selectedStore.value === 'apple' ? 'apple_store' : 'google_store'
    let filename = `${prefix}_${year}_${monthLabel}.csv`
    if (contentDisposition) {
      const match = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/)
      if (match && match[1]) filename = match[1].replace(/['"]/g, '')
    }

    const url = window.URL.createObjectURL(response.data)
    const link = document.createElement('a')
    link.href = url
    link.download = filename
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    window.URL.revokeObjectURL(url)

    showSuccess('CSV erfolgreich heruntergeladen')
  } catch (err) {
    console.error('Error downloading CSV:', err)
    showError(err.response?.data?.message || 'Fehler beim Herunterladen der CSV')
  } finally {
    downloadingCsv.value = false
  }
}

const downloadPdf = async (year, month) => {
  try {
    downloadingPdf.value = true
    const monthLabel = String(month).padStart(2, '0')
    const response =
      selectedStore.value === 'apple'
        ? await adminApi.downloadAppleStorePdf(year, monthLabel)
        : await adminApi.downloadGoogleStorePdf(year, monthLabel)
    const contentDisposition = response.headers?.['content-disposition']
    const prefix = selectedStore.value === 'apple' ? 'apple_store' : 'google_store'
    let filename = `${prefix}_${year}_${monthLabel}.pdf`
    if (contentDisposition) {
      const match = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/)
      if (match && match[1]) filename = match[1].replace(/['"]/g, '')
    }

    const url = window.URL.createObjectURL(response.data)
    const link = document.createElement('a')
    link.href = url
    link.download = filename
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    window.URL.revokeObjectURL(url)

    showSuccess('PDF erfolgreich heruntergeladen')
  } catch (err) {
    console.error('Error downloading PDF:', err)
    showError(err.response?.data?.message || 'Fehler beim Herunterladen der PDF')
  } finally {
    downloadingPdf.value = false
  }
}

const downloadZip = async (year, quarter) => {
  try {
    downloadingZip.value = true
    const response =
      selectedStore.value === 'apple'
        ? await adminApi.downloadAppleStoreZip(year, quarter)
        : await adminApi.downloadGoogleStoreZip(year, quarter)
    const contentDisposition = response.headers?.['content-disposition']
    const prefix = selectedStore.value === 'apple' ? 'apple_store' : 'google_store'
    let filename = `${prefix}_${year}_Q${quarter}.zip`
    if (contentDisposition) {
      const match = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/)
      if (match && match[1]) filename = match[1].replace(/['"]/g, '')
    }

    const url = window.URL.createObjectURL(response.data)
    const link = document.createElement('a')
    link.href = url
    link.download = filename
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    window.URL.revokeObjectURL(url)

    showSuccess('ZIP erfolgreich heruntergeladen')
  } catch (err) {
    console.error('Error downloading ZIP:', err)
    showError(err.response?.data?.message || 'Fehler beim Herunterladen der ZIP')
  } finally {
    downloadingZip.value = false
  }
}

const syncNow = async () => {
  try {
    syncStatus.value = { ...syncStatus.value, [selectedStore.value]: true }
    if (selectedStore.value === 'apple') {
      await adminApi.syncAppleStoreNow()
    } else {
      await adminApi.syncGoogleStoreNow()
    }
    showSuccess('Sync erfolgreich gestartet')
    await loadStoreData(selectedStore.value, { force: true })
  } catch (err) {
    console.error('Error syncing store:', err)
    showError(err.response?.data?.message || 'Fehler beim Sync')
  } finally {
    syncStatus.value = { ...syncStatus.value, [selectedStore.value]: false }
  }
}

watch(selectedStore, (store) => {
  loadStoreData(store)
})

onMounted(() => {
  loadStoreData('google')
})
</script>

<style scoped>
.stores-list details > summary {
  cursor: pointer;
}

.year-summary {
  display: flex;
  align-items: center;
  gap: 12px;
}

.quarter-card {
  border: 1px solid #e9ecef;
  border-radius: 8px;
  padding: 12px 16px;
  margin-bottom: 12px;
}

.quarter-summary {
  display: flex;
  align-items: center;
  gap: 12px;
  cursor: pointer;
}

.month-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
}

.month-card {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px;
  border: 1px dashed #dee2e6;
  border-radius: 10px;
  background: #f8f9fa;
}

.month-label {
  font-weight: 600;
}

.year-switcher {
  display: flex;
  align-items: center;
  gap: 12px;
}

.year-label {
  min-width: 72px;
  text-align: center;
  font-weight: 600;
  font-size: 1.1rem;
}
</style>
